// Findigs URL Builder Utility
// Handles UTM parameter passing and custom tracking parameters

import { getStableFmeId } from './fmeIdentity';

/**
 * Get VWO FME UUID for offline goals tracking
 * @returns {string|null} - VWO FME UUID or null if not available
 */
const getVWOFMEUUID = () => {
  try {
    // Use the same stable FME ID that's used throughout the codebase
    return getStableFmeId();
  } catch (error) {
    console.error('Error getting VWO FME UUID:', error);
  }
  return null;
};

/**
 * Build enhanced Findigs URL with UTM parameters and tracking data
 * @param {string} baseUrl - Base Findigs URL
 * @param {Object} params - Parameters object
 * @param {Object} params.arrivalInfo - UTM and referrer data from site visit
 * @param {string} params.buttonPosition - Which apply button was clicked
 * @param {string} params.floorplanName - Floorplan name if from floorplan page (optional)
 * @param {string} params.abTestVariation - A/B test variation ('treatment', 'control', 'not_in_test')
 * @param {string} params.flagKey - VWO flag key for campaign tracking (optional)
 * @returns {string} - Enhanced Findigs URL
 */
export const buildFindigsUrl = (baseUrl, { arrivalInfo = {}, buttonPosition = 'unknown', floorplanName = null, abTestVariation = 'not_in_test', flagKey = null }) => {
  try {
    const url = new URL(baseUrl);
    
    // Pass through original UTM parameters from site visit
    // If no utm_source exists, add default Property Website parameters
    if (arrivalInfo.utmSource) {
      url.searchParams.set('utm_source', arrivalInfo.utmSource);
    } else if (!url.searchParams.has('utm_source')) {
      // Add default Property Website UTM parameters when no source is present
      url.searchParams.set('utm_source', 'Property Website');
      url.searchParams.set('utm_medium', 'website');
      url.searchParams.set('utm_campaign', 'apply_link');
    }
    
    // Only set other UTM parameters if they exist (don't override defaults)
    if (arrivalInfo.utmMedium) {
      url.searchParams.set('utm_medium', arrivalInfo.utmMedium);
    }
    
    if (arrivalInfo.utmCampaign) {
      url.searchParams.set('utm_campaign', arrivalInfo.utmCampaign);
    }
    
    if (arrivalInfo.utmTerm) {
      url.searchParams.set('utm_term', arrivalInfo.utmTerm);
    }
    
    if (arrivalInfo.utmContent) {
      url.searchParams.set('utm_content', arrivalInfo.utmContent);
    }
    
    // Add button position as SOURCE parameter for tracking which button was clicked
    url.searchParams.set('source', `apply_button_${buttonPosition}`);
    
    // Add floorplan name as utm_referrer if coming from floorplan apply link
    if (floorplanName) {
      url.searchParams.set('utm_referrer', floorplanName);
    }
    
    // Add session ID for reconciliation
    if (arrivalInfo.sessionId) {
      url.searchParams.set('session_id', arrivalInfo.sessionId);
    }
    
    // Add VWO FME UUID for offline goals tracking via mibextid parameter
    // Only for properties participating in A/B test (treatment or control)
    if (abTestVariation === 'treatment' || abTestVariation === 'control') {
      const fmeUUID = getVWOFMEUUID();
      if (fmeUUID) {
        url.searchParams.set('mibextid', fmeUUID);
      }
    }
    
    // Add VWO flag key for campaign tracking
    // This helps Findigs identify which VWO campaign the user came from
    if (flagKey) {
      url.searchParams.set('SSP', flagKey);
    }
    
    return url.toString();
    
  } catch (error) {
    console.error('Error building Findigs URL:', error);
    return baseUrl; // Return original URL as fallback
  }
};

/**
 * Extract floorplan name from current page context or URL
 * @param {string} currentPath - Current page path
 * @param {Object} pageContext - Gatsby page context (optional)
 * @returns {string|null} - Floorplan name or null
 */
export const extractFloorplanName = (currentPath, pageContext = {}) => {
  try {
    // Check if we're on a floorplan page
    if (currentPath && currentPath.includes('/floorplans/')) {
      // Extract floorplan name from URL path
      const pathParts = currentPath.split('/');
      const floorplanIndex = pathParts.indexOf('floorplans');
      if (floorplanIndex !== -1 && pathParts[floorplanIndex + 1]) {
        return pathParts[floorplanIndex + 1];
      }
    }
    
    // Check page context for floorplan data
    if (pageContext.floorplanName) {
      return pageContext.floorplanName;
    }
    
    if (pageContext.floorplan && pageContext.floorplan.name) {
      return pageContext.floorplan.name;
    }
    
    // Check URL hash or query params for floorplan info
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const floorplanParam = urlParams.get('floorplan') || urlParams.get('plan');
      if (floorplanParam) {
        return floorplanParam;
      }
    }
    
    return null;
    
  } catch (error) {
    console.error('Error extracting floorplan name:', error);
    return null;
  }
};

/**
 * Helper to detect if current context is a floorplan page
 * @param {string} currentPath - Current page path
 * @param {string} buttonPosition - Button position
 * @returns {boolean} - True if on floorplan page or floorplan button
 */
export const isFloorplanContext = (currentPath, buttonPosition) => {
  return (
    (currentPath && currentPath.includes('/floorplans/')) ||
    buttonPosition === 'floorplans'
  );
};